/**
 * CapitalCalls.js — Capital Call & Distribution Workflow
 * =======================================================
 * Bug #28: Capital call issuance, tracking, distribution waterfall
 *  - Issue capital calls to LPs
 *  - Track call notices vs payments received
 *  - Distribution workflow with waterfall preview
 *  - Per-LP commitment/funded/unfunded tracking
 */

import React, { useState, useEffect, useCallback } from "react";
import api from "../api/client";
import FinTable from "../components/FinTable";

const C = {
  bg: "#0f1724", card: "#1a2332", border: "#2a3a4e", text: "#e2e8f0",
  muted: "#94a3b8", accent: "#3b82f6", green: "#22c55e", greenBg: "rgba(34,197,94,.12)",
  red: "#ef4444", redBg: "rgba(239,68,68,.12)", orange: "#f59e0b",
  orangeBg: "rgba(245,158,11,.12)", purple: "#a855f7", purpleBg: "rgba(168,85,247,.12)",
  cyan: "#06b6d4", cyanBg: "rgba(6,182,212,.12)",
};

const fmtUSD = (v) => v != null ? Number(v).toLocaleString("en-US", { style: "currency", currency: "USD" }) : "—";
const fmtPct = (v) => v != null ? `${Number(v).toFixed(2)}%` : "—";

const Badge = ({ children, bg, color }) => (
  <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 4, fontSize: 11, fontWeight: 600, background: bg, color, textTransform: "uppercase", letterSpacing: ".5px" }}>{children}</span>
);
const KPI = ({ label, value, sublabel, color, icon }) => (
  <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "18px 22px", flex: 1, minWidth: 180 }}>
    <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 8 }}>{icon} {label}</div>
    <div style={{ fontSize: 24, fontWeight: 700, color: color || C.text }}>{value}</div>
    {sublabel && <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{sublabel}</div>}
  </div>
);

const Modal = ({ open, onClose, title, children, width = 540 }) => {
  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,.6)", backdropFilter: "blur(4px)" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, width, maxWidth: "95vw", maxHeight: "85vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,.5)" }}>
        <div style={{ padding: "18px 24px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0, fontSize: 17, color: C.text, fontWeight: 600 }}>{title}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", color: C.muted, fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ padding: "20px 24px" }}>{children}</div>
      </div>
    </div>
  );
};

const InputField = ({ label, value, onChange, type = "text", placeholder, hint }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 6, textTransform: "uppercase", letterSpacing: ".5px" }}>{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: `1px solid ${C.border}`, background: C.bg, color: C.text, fontSize: 14, boxSizing: "border-box" }} />
    {hint && <span style={{ fontSize: 11, color: C.muted, marginTop: 4, display: "block" }}>{hint}</span>}
  </div>
);

const SelectField = ({ label, value, onChange, options, placeholder }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 6, textTransform: "uppercase", letterSpacing: ".5px" }}>{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)}
      style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: `1px solid ${C.border}`, background: C.bg, color: C.text, fontSize: 14, boxSizing: "border-box" }}>
      <option value="">{placeholder || "Select…"}</option>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

export default function CapitalCalls() {
  const [lpAccounts, setLpAccounts] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [distributions, setDistributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [toast, setToast] = useState(null);
  const [callModal, setCallModal] = useState(false);
  const [distModal, setDistModal] = useState(false);
  const [callForm, setCallForm] = useState({ lp_account: "", deal: "", amount: "", call_date: new Date().toISOString().slice(0, 10), due_date: "", notes: "" });
  const [distForm, setDistForm] = useState({ lp_account: "", deal: "", amount: "", distribution_date: new Date().toISOString().slice(0, 10), distribution_type: "ReturnOfCapital", notes: "" });

  const showToast = (msg, type = "success") => { setToast({ msg, type }); setTimeout(() => setToast(null), 4000); };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [lpRes, invRes, distRes] = await Promise.all([
        api.get("/investors/lp-accounts/?limit=100"),
        api.get("/investors/investments/?limit=500"),
        api.get("/investors/distributions/?limit=500"),
      ]);
      setLpAccounts(lpRes.data.results || lpRes.data);
      setInvestments(invRes.data.results || invRes.data);
      setDistributions(distRes.data.results || distRes.data);
    } catch (err) {
      console.error("Capital calls load failed:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Build LP summary
  const lpSummary = lpAccounts.map(lp => {
    const invs = investments.filter(i => i.lp_account === lp.id);
    const dists = distributions.filter(d => d.lp_account === lp.id);
    const committed = Number(lp.committed_capital || 0);
    const funded = invs.reduce((s, i) => s + Number(i.invested_amount || 0), 0);
    const distributed = dists.reduce((s, d) => s + Number(d.amount || 0), 0);
    const unfunded = Math.max(0, committed - funded);

    return {
      ...lp,
      name: lp.investor_name || `LP #${lp.id}`,
      committed,
      funded,
      distributed,
      unfunded,
      investment_count: invs.length,
      distribution_count: dists.length,
      funded_pct: committed > 0 ? (funded / committed * 100) : 0,
    };
  });

  // KPIs
  const totalCommitted = lpSummary.reduce((s, l) => s + l.committed, 0);
  const totalFunded = lpSummary.reduce((s, l) => s + l.funded, 0);
  const totalDistributed = lpSummary.reduce((s, l) => s + l.distributed, 0);
  const totalUnfunded = lpSummary.reduce((s, l) => s + l.unfunded, 0);

  // Capital call handler
  const handleIssueCall = async () => {
    try {
      await api.post("/investors/investments/", {
        lp_account: Number(callForm.lp_account),
        deal: Number(callForm.deal),
        invested_amount: callForm.amount,
        investment_date: callForm.call_date,
        notes: callForm.notes || `Capital call issued ${callForm.call_date}`,
      });
      showToast("Capital call recorded");
      setCallModal(false);
      loadData();
    } catch (err) {
      showToast(err.response?.data ? JSON.stringify(err.response.data) : "Failed", "error");
    }
  };

  // Distribution handler
  const handleDistribution = async () => {
    try {
      await api.post("/investors/distributions/", {
        lp_account: Number(distForm.lp_account),
        deal: Number(distForm.deal),
        amount: distForm.amount,
        distribution_date: distForm.distribution_date,
        distribution_type: distForm.distribution_type,
        notes: distForm.notes,
      });
      showToast("Distribution recorded");
      setDistModal(false);
      loadData();
    } catch (err) {
      showToast(err.response?.data ? JSON.stringify(err.response.data) : "Failed", "error");
    }
  };

  const lpOpts = lpAccounts.map(l => ({ value: l.id, label: l.investor_name || `LP #${l.id}` }));
  // Deals from investments
  const dealIds = [...new Set(investments.map(i => i.deal))];

  return (
    <div style={{ padding: "0 24px 40px" }}>
      {toast && (
        <div style={{ position: "fixed", top: 20, right: 20, zIndex: 2000, padding: "12px 20px", borderRadius: 8, background: toast.type === "error" ? C.red : C.green, color: "#fff", fontSize: 14, fontWeight: 600, boxShadow: "0 8px 30px rgba(0,0,0,.4)" }}>{toast.msg}</div>
      )}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: C.text, margin: 0 }}>Capital Calls & Distributions</h1>
          <p style={{ color: C.muted, fontSize: 14, margin: "4px 0 0" }}>LP commitments, capital calls, and distribution tracking</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setCallModal(true)} style={{ padding: "8px 20px", borderRadius: 6, border: "none", background: C.green, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>+ Capital Call</button>
          <button onClick={() => setDistModal(true)} style={{ padding: "8px 20px", borderRadius: 6, border: "none", background: C.purple, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>+ Distribution</button>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: "flex", gap: 14, marginBottom: 24, flexWrap: "wrap" }}>
        <KPI label="Total Committed" value={fmtUSD(totalCommitted)} icon="📋" sublabel={`${lpSummary.length} LPs`} />
        <KPI label="Total Funded" value={fmtUSD(totalFunded)} color={C.green} icon="✅" sublabel={totalCommitted > 0 ? `${(totalFunded / totalCommitted * 100).toFixed(1)}% called` : ""} />
        <KPI label="Unfunded" value={fmtUSD(totalUnfunded)} color={C.orange} icon="⏳" sublabel="Remaining commitments" />
        <KPI label="Distributions" value={fmtUSD(totalDistributed)} color={C.purple} icon="💸" sublabel={`${distributions.length} payments`} />
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20 }}>
        {[
          { key: "overview", label: "LP Overview", count: lpSummary.length },
          { key: "calls", label: "Capital Calls", count: investments.length },
          { key: "distributions", label: "Distributions", count: distributions.length },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: "8px 20px", borderRadius: 20, border: `1px solid ${tab === t.key ? C.accent : C.border}`,
            background: tab === t.key ? C.accent : "transparent", color: tab === t.key ? "#fff" : C.muted,
            fontSize: 13, fontWeight: 600, cursor: "pointer",
          }}>{t.label} ({t.count})</button>
        ))}
      </div>

      {/* LP Overview */}
      {tab === "overview" && (
        <FinTable
          columns={[
            { key: "name", label: "Investor", sortable: true },
            { key: "committed", label: "Committed", align: "right", sortable: true, fmt: fmtUSD },
            { key: "funded", label: "Funded", align: "right", sortable: true,
              render: (v) => <span style={{ color: C.green, fontWeight: 600 }}>{fmtUSD(v)}</span> },
            { key: "unfunded", label: "Unfunded", align: "right", sortable: true,
              render: (v) => <span style={{ color: v > 0 ? C.orange : C.muted, fontWeight: 600 }}>{fmtUSD(v)}</span> },
            { key: "funded_pct", label: "% Called", align: "right", sortable: true,
              render: (v) => {
                const pct = Number(v);
                return (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
                    <div style={{ width: 60, height: 6, borderRadius: 3, background: C.border, overflow: "hidden" }}>
                      <div style={{ width: `${Math.min(100, pct)}%`, height: "100%", borderRadius: 3, background: pct >= 100 ? C.green : C.accent }} />
                    </div>
                    <span style={{ fontSize: 12, color: C.muted, minWidth: 40, textAlign: "right" }}>{pct.toFixed(0)}%</span>
                  </div>
                );
              }},
            { key: "distributed", label: "Distributed", align: "right", sortable: true,
              render: (v) => <span style={{ color: C.purple }}>{fmtUSD(v)}</span> },
            { key: "investment_count", label: "Calls", align: "right", sortable: true },
          ]}
          rows={lpSummary}
          loading={loading}
          pageSize={25}
          defaultSort={{ key: "committed", direction: "desc" }}
          emptyMessage="No LP accounts found. Add investors on the Investors page."
        />
      )}

      {/* Capital Calls */}
      {tab === "calls" && (
        <FinTable
          columns={[
            { key: "investor_name", label: "Investor", sortable: true },
            { key: "deal_name", label: "Deal", sortable: true, truncate: 22 },
            { key: "invested_amount", label: "Amount", align: "right", sortable: true, fmt: fmtUSD },
            { key: "ownership_pct", label: "Ownership %", align: "right", sortable: true, fmt: fmtPct },
            { key: "investment_date", label: "Date", sortable: true },
            { key: "notes", label: "Notes", truncate: 30 },
          ]}
          rows={investments}
          loading={loading}
          pageSize={25}
          defaultSort={{ key: "investment_date", direction: "desc" }}
          emptyMessage="No capital calls recorded."
        />
      )}

      {/* Distributions */}
      {tab === "distributions" && (
        <FinTable
          columns={[
            { key: "investor_name", label: "Investor", sortable: true },
            { key: "deal_name", label: "Deal", sortable: true, truncate: 22 },
            { key: "amount", label: "Amount", align: "right", sortable: true,
              render: (v) => <span style={{ color: C.purple, fontWeight: 600 }}>{fmtUSD(v)}</span> },
            { key: "distribution_type", label: "Type", sortable: true,
              render: (v) => <Badge bg={v === "ReturnOfCapital" ? C.cyanBg : C.greenBg} color={v === "ReturnOfCapital" ? C.cyan : C.green}>{(v || "").replace(/([A-Z])/g, " $1").trim()}</Badge> },
            { key: "distribution_date", label: "Date", sortable: true },
            { key: "notes", label: "Notes", truncate: 30 },
          ]}
          rows={distributions}
          loading={loading}
          pageSize={25}
          defaultSort={{ key: "distribution_date", direction: "desc" }}
          emptyMessage="No distributions recorded."
        />
      )}

      {/* Capital Call Modal */}
      <Modal open={callModal} onClose={() => setCallModal(false)} title="Issue Capital Call">
        <SelectField label="Investor" value={callForm.lp_account} onChange={v => setCallForm(p => ({ ...p, lp_account: v }))} options={lpOpts} />
        <InputField label="Amount" type="number" value={callForm.amount} onChange={v => setCallForm(p => ({ ...p, amount: v }))} placeholder="500,000" />
        <InputField label="Call Date" type="date" value={callForm.call_date} onChange={v => setCallForm(p => ({ ...p, call_date: v }))} />
        <InputField label="Due Date" type="date" value={callForm.due_date} onChange={v => setCallForm(p => ({ ...p, due_date: v }))} hint="When payment is expected" />
        <InputField label="Notes" value={callForm.notes} onChange={v => setCallForm(p => ({ ...p, notes: v }))} placeholder="Capital call notice #..." />
        <button onClick={handleIssueCall} style={{ width: "100%", padding: 12, borderRadius: 8, border: "none", background: C.green, color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", marginTop: 8 }}>Issue Capital Call</button>
      </Modal>

      {/* Distribution Modal */}
      <Modal open={distModal} onClose={() => setDistModal(false)} title="Record Distribution">
        <SelectField label="Investor" value={distForm.lp_account} onChange={v => setDistForm(p => ({ ...p, lp_account: v }))} options={lpOpts} />
        <InputField label="Amount" type="number" value={distForm.amount} onChange={v => setDistForm(p => ({ ...p, amount: v }))} placeholder="100,000" />
        <SelectField label="Type" value={distForm.distribution_type} onChange={v => setDistForm(p => ({ ...p, distribution_type: v }))}
          options={[
            { value: "ReturnOfCapital", label: "Return of Capital" },
            { value: "IncentiveFee", label: "Incentive Fee Distribution" },
            { value: "Dividend", label: "Dividend" },
            { value: "Other", label: "Other" },
          ]} />
        <InputField label="Date" type="date" value={distForm.distribution_date} onChange={v => setDistForm(p => ({ ...p, distribution_date: v }))} />
        <InputField label="Notes" value={distForm.notes} onChange={v => setDistForm(p => ({ ...p, notes: v }))} placeholder="Distribution memo..." />
        <button onClick={handleDistribution} style={{ width: "100%", padding: 12, borderRadius: 8, border: "none", background: C.purple, color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", marginTop: 8 }}>Record Distribution</button>
      </Modal>
    </div>
  );
}
